// Pure logic for the Library view (docs/plan/phase-10-ui.md, "Library"):
// classifying an artifact's media kind, grouping the list by trial/day,
// summarizing a group's shared attrs, the type/role filters that are
// applied client-side, and the URL-query round trip for whichever
// filters are active. Kept framework-free, like lib/generate.ts and
// lib/results.ts, so each piece is unit-testable without rendering the
// view.
import type { ArtifactListItem, MediaInfo } from "../api/client";
import { extensionFor, formatBytes } from "./results";

// ---- media kind -----------------------------------------------------------

export type MediaKind = "image" | "video" | "other";

/** What kind of thing an artifact is, for picking a preview widget. The
 * probed `media.kind` (ADR-0021, derived by Store from the bytes) wins when
 * it is image or video; otherwise the declared content_type decides. A
 * worker that uploaded a clip as application/octet-stream (every output
 * was, before 2026-10-10) therefore still shows as a video, not as "BIN". */
export function mediaKindOf(item: MediaKindSource | string | null | undefined): MediaKind {
  if (typeof item === "object" && item !== null) {
    const probed = item.media?.kind;
    if (probed === "image" || probed === "video") return probed;
    return mediaKindOf(item.content_type);
  }
  const contentType = item;
  if (!contentType) return "other";
  if (contentType.startsWith("image/")) return "image";
  if (contentType.startsWith("video/")) return "video";
  return "other";
}

export type MediaKindSource = { content_type?: string | null; media?: MediaInfo };

// ---- server-derived media metadata (ADR-0021) ------------------------------
//
// `MediaKind` above is a client-side guess from content_type alone, used
// for picking a preview widget. `item.media` (when present) is the real
// thing: facts Store derived from the artifact's own bytes. These helpers
// turn it into the short strings the tile footer and the viewer's detail
// list show.

/** Seconds as "m:ss" (or "h:mm:ss" past an hour), rounded to the nearest
 * second. A negative or non-finite input (should not happen, but a
 * malformed/partial probe result is possible) renders as "0:00" rather
 * than throwing or printing garbage. */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** "1920×1080" when media has both dimensions, else undefined -- media
 * with no width/height (a plain audio file, or a format this package
 * could not parse) has nothing to show here. */
export function mediaDimensions(media?: MediaInfo): string | undefined {
  if (!media?.width || !media?.height) return undefined;
  return `${media.width}×${media.height}`;
}

/** The tile footer's caption when media is present (Library, "tile
 * footer shows W×H for images/videos and m:ss duration for video/audio"):
 * dimensions and/or duration, whichever the kind and the probe provide,
 * joined; undefined when media is absent or empty (KindOther with
 * nothing else), so the caller falls back to its own default (file
 * size). */
export function mediaCaption(media?: MediaInfo): string | undefined {
  if (!media) return undefined;
  const parts: string[] = [];
  const dims = mediaDimensions(media);
  if (dims) parts.push(dims);
  if (typeof media.duration_s === "number" && (media.kind === "video" || media.kind === "audio")) {
    parts.push(formatDuration(media.duration_s));
  }
  return parts.length > 0 ? parts.join(" · ") : undefined;
}

/** The tile footer's meta line: media's own caption when there is one,
 * else the file size Library showed before ADR-0021, else nothing. */
export function tileMetaLabel(item: ArtifactListItem): string {
  return mediaCaption(item.media) ?? (item.size ? formatBytes(item.size) : "");
}

export interface MediaDetailRow {
  label: string;
  value: string;
}

/** The viewer drawer's media detail rows (Library: "the viewer's detail
 * list shows the media fields -- format, dimensions, orientation/
 * rotation, duration, codecs, fps, audio"). Empty when media is absent;
 * a field the format did not provide is simply not a row, same as the
 * API's own omission convention. */
export function mediaDetailRows(media?: MediaInfo): MediaDetailRow[] {
  if (!media) return [];
  const rows: MediaDetailRow[] = [];
  if (media.format) rows.push({ label: "Format", value: media.format.toUpperCase() });
  const dims = mediaDimensions(media);
  if (dims) rows.push({ label: "Dimensions", value: dims });
  if (media.orientation && media.orientation !== 1) {
    rows.push({ label: "Orientation", value: String(media.orientation) });
  }
  if (media.rotation) rows.push({ label: "Rotation", value: `${media.rotation}°` });
  if (typeof media.duration_s === "number") {
    rows.push({ label: "Duration", value: formatDuration(media.duration_s) });
  }
  if (media.vcodec) rows.push({ label: "Video codec", value: media.vcodec });
  if (typeof media.fps === "number" && media.fps > 0) {
    rows.push({ label: "Frame rate", value: `${Math.round(media.fps * 100) / 100} fps` });
  }
  if (media.animated) {
    rows.push({ label: "Frames", value: media.frames ? String(media.frames) : "animated" });
  }
  const audioParts: string[] = [];
  if (media.acodec) audioParts.push(media.acodec);
  if (media.sample_rate) audioParts.push(`${media.sample_rate} Hz`);
  if (media.channels) audioParts.push(media.channels === 1 ? "mono" : media.channels === 2 ? "stereo" : `${media.channels}ch`);
  if ((media.kind === "audio" || media.has_audio) && audioParts.length > 0) {
    rows.push({ label: "Audio", value: audioParts.join(", ") });
  }
  if (media.has_alpha) rows.push({ label: "Alpha channel", value: "yes" });
  if (media.color_space) rows.push({ label: "Color space", value: media.color_space });
  if (media.content_type_mismatch) rows.push({ label: "Content type mismatch", value: "yes" });
  if (media.error) rows.push({ label: "Probe note", value: media.error });
  return rows;
}

// ---- filters ----------------------------------------------------------

export type TypeFilter = "all" | "image" | "video" | "other";
export type RoleFilter = "all" | "source" | "result";

/** The `content_type_prefix` query param for a type filter. "all" and
 * "other" both fetch everything from the server -- "other" (neither
 * image nor video) has no prefix of its own, so it is filtered client
 * side after the fact by `matchesTypeFilter`. */
export function contentTypePrefixFor(filter: TypeFilter): string | undefined {
  if (filter === "image") return "image/";
  if (filter === "video") return "video/";
  return undefined;
}

export function matchesTypeFilter(item: ArtifactListItem, filter: TypeFilter): boolean {
  if (filter === "all") return true;
  return mediaKindOf(item) === filter;
}

export function roleOf(item: ArtifactListItem): string | undefined {
  const role = item.attrs?.role;
  return role === undefined || role === null ? undefined : String(role);
}

/** The role a filter sees: the `role` attr when an upload set one (the
 * harness's sweeps), else the artifact's kind -- a Task's OUTPUT is a
 * result, an uploaded INPUT a source. Ordinary runs set no attr, so the
 * attr alone hid every clip the Studio made (2026-10-22). */
export function effectiveRole(item: ArtifactListItem): string | undefined {
  const role = roleOf(item);
  if (role !== undefined) return role;
  if (item.kind === "OUTPUT") return "result";
  if (item.kind === "INPUT") return "source";
  return undefined;
}

export function matchesRoleFilter(item: ArtifactListItem, filter: RoleFilter): boolean {
  if (filter === "all") return true;
  return effectiveRole(item) === filter;
}

export function visibleItems(items: ArtifactListItem[], type: TypeFilter, role: RoleFilter): ArtifactListItem[] {
  return items.filter((it) => matchesTypeFilter(it, type) && matchesRoleFilter(it, role));
}

// ---- URL query round trip -----------------------------------------------

export interface LibraryFilters {
  type: TypeFilter;
  role: RoleFilter;
  q: string;
  trial?: string;
}

// The Library opens on Results (user, 2026-10-22): the clips you made, not
// the photos you fed in. "All" is now an explicit `?role=all`.
export const DEFAULT_LIBRARY_FILTERS: LibraryFilters = { type: "all", role: "result", q: "" };

function isTypeFilter(v: string | null): v is TypeFilter {
  return v === "image" || v === "video" || v === "other" || v === "all";
}

function isRoleFilter(v: string | null): v is RoleFilter {
  return v === "source" || v === "result" || v === "all";
}

/** Read the Library's filters back out of `?type=&role=&q=&trial=`, so a
 * reload or a shared link lands on the same view. Anything unrecognized
 * falls back to the default rather than throwing. */
export function filtersFromSearchParams(params: URLSearchParams): LibraryFilters {
  const type = params.get("type");
  const role = params.get("role");
  const trial = params.get("trial");
  return {
    type: isTypeFilter(type) ? type : "all",
    role: isRoleFilter(role) ? role : DEFAULT_LIBRARY_FILTERS.role,
    q: params.get("q") ?? "",
    trial: trial || undefined,
  };
}

/** The inverse of `filtersFromSearchParams`: only non-default filters are
 * written, so a plain `/library` stays a plain `/library`. */
export function searchParamsFromFilters(filters: LibraryFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.type !== "all") params.set("type", filters.type);
  if (filters.role !== DEFAULT_LIBRARY_FILTERS.role) params.set("role", filters.role);
  if (filters.q) params.set("q", filters.q);
  if (filters.trial) params.set("trial", filters.trial);
  return params;
}

// ---- grouping -----------------------------------------------------------

export interface LibraryGroup {
  key: string;
  kind: "trial" | "day";
  /** Set when `kind === "trial"`: the raw `attrs.trial` value. */
  trial?: string;
  /** Set when `kind === "day"`: a local calendar day, "YYYY-MM-DD". */
  day?: string;
  items: ArtifactListItem[];
}

export function trialOf(item: ArtifactListItem): string | undefined {
  const trial = item.attrs?.trial;
  return trial === undefined || trial === null || trial === "" ? undefined : String(trial);
}

/** The local calendar day an ISO timestamp falls on, "YYYY-MM-DD" --
 * grouping key for items with no trial. An unparseable timestamp gets its
 * own "unknown" bucket rather than throwing or being dropped. */
export function dayOf(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "unknown";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Group items by trial where they have one, otherwise by day
 * (docs/plan/phase-10-ui.md "Library": "if an item has attrs.trial, group
 * by trial ... items without a trial grouped by day"). Groups are ordered
 * by first occurrence in `items`, which the API already returns newest
 * first, so the newest group leads. A trial's items need not be
 * contiguous in that order -- concurrent trials interleave -- so this
 * builds every group in one pass keyed by trial id / day rather than
 * chunking consecutive runs. */
export function groupLibraryItems(items: ArtifactListItem[]): LibraryGroup[] {
  const order: string[] = [];
  const groups = new Map<string, LibraryGroup>();
  for (const item of items) {
    const trial = trialOf(item);
    const key = trial !== undefined ? `trial:${trial}` : `day:${dayOf(item.created_at)}`;
    let group = groups.get(key);
    if (!group) {
      group =
        trial !== undefined
          ? { key, kind: "trial", trial, items: [] }
          : { key, kind: "day", day: dayOf(item.created_at), items: [] };
      groups.set(key, group);
      order.push(key);
    }
    group.items.push(item);
  }
  return order.map((key) => groups.get(key) as LibraryGroup);
}

/** The rendered order flattened back out, for keyboard left/right
 * navigation in the viewer -- the order tiles actually appear on screen,
 * which is not always the fetch order once trials interleave with
 * ungrouped days (see groupLibraryItems). */
export function flattenGroups(groups: LibraryGroup[]): ArtifactListItem[] {
  return groups.flatMap((g) => g.items);
}

// ---- group summary --------------------------------------------------------

export interface GroupSummary {
  total: number;
  sources: number;
  results: number;
  /** Attrs every item in the group shares the same value for, restricted
   * to the small set of attrs worth showing in a header line. */
  commonAttrs: Record<string, string | number | boolean>;
}

const SUMMARY_ATTR_KEYS = ["model", "precision", "gpu", "provider", "machine"];

export function summarizeGroup(items: ArtifactListItem[]): GroupSummary {
  let sources = 0;
  let results = 0;
  for (const item of items) {
    const role = roleOf(item);
    if (role === "source") sources += 1;
    else if (role === "result") results += 1;
  }
  const commonAttrs: Record<string, string | number | boolean> = {};
  for (const key of SUMMARY_ATTR_KEYS) {
    if (items.length === 0) break;
    const first = items[0].attrs?.[key];
    if (first === undefined) continue;
    if (items.every((it) => it.attrs?.[key] === first)) {
      commonAttrs[key] = first;
    }
  }
  return { total: items.length, sources, results, commonAttrs };
}

/** A group header's summary line: "12 files · 8 sources · 4 results ·
 * model echo_v2_dev · gpu a100" -- counts first, then whichever attrs every
 * item in the group agrees on. */
export function summaryLine(summary: GroupSummary): string {
  const parts: string[] = [`${summary.total} file${summary.total === 1 ? "" : "s"}`];
  if (summary.sources) parts.push(`${summary.sources} source${summary.sources === 1 ? "" : "s"}`);
  if (summary.results) parts.push(`${summary.results} result${summary.results === 1 ? "" : "s"}`);
  for (const [key, value] of Object.entries(summary.commonAttrs)) parts.push(`${key} ${value}`);
  return parts.join(" · ");
}

// ---- display helpers ------------------------------------------------------

const DAY_HEADING = new Intl.DateTimeFormat(undefined, { year: "numeric", month: "long", day: "numeric" });

/** "YYYY-MM-DD" -> "September 19, 2026", read back as a local date so it
 * prints the same day it was grouped under. */
export function formatDayHeading(day: string): string {
  if (day === "unknown") return "Unknown date";
  const [y, m, d] = day.split("-").map(Number);
  if (!y || !m || !d) return day;
  return DAY_HEADING.format(new Date(y, m - 1, d));
}

/** Alt text for a tile: the label the uploader gave it, or a plain
 * description of what kind of file it is -- never nothing. */
export function tileAltText(item: ArtifactListItem): string {
  if (item.label) return item.label;
  const kind = mediaKindOf(item);
  if (kind === "image") return "image file";
  if (kind === "video") return "video file";
  return item.content_type ? `${item.content_type} file` : "file";
}

/** A file name to save a downloaded artifact under: its label if it has
 * one (without a doubled extension), else its id, with the extension its
 * content type implies. `downloadFileName` (lib/results.ts) still has to
 * flatten this before it goes in an `<a download>`, same as a task
 * result. */
export function fileNameFor(item: ArtifactListItem): string {
  const ext = extensionFor(item.content_type);
  const base = item.label?.trim() || item.id.slice(0, 8);
  return base.toLowerCase().endsWith("." + ext) ? base : `${base}.${ext}`;
}
